import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { usePager, Pager } from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const GST_CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
function validGSTIN(g) {
  g = (g || "").trim().toUpperCase();
  if (!GSTIN_RE.test(g)) return false;
  let total = 0;
  for (let i = 0; i < 14; i++) {
    const p = GST_CHARS.indexOf(g[i]) * (i % 2 ? 2 : 1);
    total += Math.floor(p / 36) + (p % 36);
  }
  return GST_CHARS[(36 - (total % 36)) % 36] === g[14];
}

const empty = { name: "", gstin: "", contact_person: "", phone: "", tracking_url: "", notes: "" };

export default function Transporters() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/transporters").then((r) => setList(r.data));
  const pager = usePager(list);
  useEffect(() => { load(); }, []);

  const gstStatus = form.gstin ? (validGSTIN(form.gstin) ? "valid" : "invalid") : null;

  const save = async () => {
    if (!form.name.trim()) { toast.error("Transporter name is required"); return; }
    if (form.gstin && !validGSTIN(form.gstin)) { toast.error("Transporter GST number is invalid"); return; }
    try {
      if (editing) await api.put(`/transporters/${editing}`, form);
      else await api.post("/transporters", form);
      toast.success("Transporter saved");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const edit = (t) => { setEditing(t.id); setForm({ ...empty, ...t }); setOpen(true); };
  const del = async (id) => { if (!window.confirm("Delete transporter?")) return; await api.delete(`/transporters/${id}`); load(); };

  return (
    <div className="space-y-4" data-testid="transporters-page">
      <PageHeader title="Transporters" subtitle="Transport companies & GST numbers for E-Way bills"
        right={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}
                      className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="t-new">
                <Plus size={14} className="mr-1" /> New Transporter
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[96vw] sm:max-w-[96vw] h-[94vh] max-h-[94vh] overflow-y-auto content-start lg:left-[calc(16rem+10px)] lg:translate-x-0 lg:top-[10px] lg:translate-y-0 lg:w-[calc(100vw-16rem-20px)] lg:max-w-[calc(100vw-16rem-20px)] lg:h-[calc(100vh-20px)] lg:max-h-[calc(100vh-20px)] lg:rounded-xl shadow-2xl dialog-slide-right">
              <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Transporter</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Company Name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="t-name" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">GST Number (for E-Way Bill)</Label>
                  <Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
                         maxLength={15} className="mt-1.5 font-mono" data-testid="t-gstin" />
                  {gstStatus === "valid" && <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1" data-testid="t-gst-valid"><CheckCircle2 size={12} /> Valid GSTIN</p>}
                  {gstStatus === "invalid" && <p className="text-xs text-rose-600 mt-1 flex items-center gap-1" data-testid="t-gst-invalid"><XCircle size={12} /> Invalid GSTIN</p>}
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Contact Person</Label>
                  <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Tracking Link</Label>
                  <Input value={form.tracking_url} onChange={(e) => setForm({ ...form, tracking_url: e.target.value })}
                         placeholder="https://www.trackon.in/track?lr={lr}" className="mt-1.5 font-mono text-xs" data-testid="t-tracking-url" />
                  <p className="text-[11px] text-slate-400 mt-1">Paste the transporter's tracking page URL. Put <code className="bg-slate-100 px-1 rounded">{"{lr}"}</code> where the LR/Docket number goes — it will be auto-filled on Dispatch.</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1.5" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="t-save">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        } />

      <div className="border border-slate-100 rounded-xl bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.04)]">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Company</th>
              <th className="text-left px-4 py-2.5">GSTIN</th>
              <th className="text-left px-4 py-2.5 hidden md:table-cell">Contact</th>
              <th className="text-left px-4 py-2.5 hidden lg:table-cell">Tracking Link</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pager.pageItems.map((t) => (
              <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50/70" data-testid={`t-row-${t.id}`}>
                <td className="px-4 py-3 font-medium text-slate-800">{t.name}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-700">{t.gstin || "-"}</td>
                <td className="px-4 py-3 hidden md:table-cell text-slate-600">
                  <div>{t.contact_person}</div>
                  <div className="text-xs text-slate-500">{t.phone}</div>
                </td>
                <td className="px-4 py-3 hidden lg:table-cell text-xs text-slate-500 max-w-[220px] truncate font-mono">{t.tracking_url || "-"}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => edit(t)} className="text-slate-500 hover:text-blue-600 mr-2"><Pencil size={14} /></button>
                  <button onClick={() => del(t.id)} className="text-slate-500 hover:text-rose-600"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-sm text-slate-500">No transporters yet.</td></tr>}
          </tbody>
        </table>
        <Pager pager={pager} />
      </div>
    </div>
  );
}
